import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ProjectId, UserId } from 'api/models';
import { SocketData } from 'api/pushes';
import {
  SocketDataForTeam,
  TeamSocketIdentifier,
  UserSocketIdentifier,
} from 'api/resource-management/types';
import { InvitedMembers } from 'data/members/slice';
import {
  useNativeTeamsInAccount,
  useRemoveTeamFromLocalState,
} from 'queries/placeholder';
import { logger } from 'services';
import { add, remove } from 'stores/memberships';
import { useAddTeamToProjectWSHandler } from './use-add-team-to-project-ws-handler';
import { useWorker } from './use-worker';

interface ReturnProps {
  handleMembersAdd: (
    socketData: SocketData<(InvitedMembers | SocketDataForTeam)[]>,
  ) => void;
  handleMembersDelete: (
    socketData: SocketData<(UserSocketIdentifier | TeamSocketIdentifier)[]>,
  ) => void;
}

export const useProjectMembersWSHandler = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}): ReturnProps => {
  const { workerApi } = useWorker();
  const dispatch = useDispatch();

  const { data: teams = [] } = useNativeTeamsInAccount();
  const teamLookup = useMemo(() => keyBy(teams, 'teamId'), [teams]);
  const addTeamToProjectSocketHandler = useAddTeamToProjectWSHandler();
  const removeTeamFromLocalState = useRemoveTeamFromLocalState();

  const addUserHandler = useCallback(
    (
      projectId: number,
      {
        user: { userId },
        permissionSet: { permissionSetType: memberType },
        joinedAt,
      }: InvitedMembers,
    ) => {
      dispatch(
        add({
          projectId: ProjectId(projectId),
          memberships: [{ userId: UserId(userId), memberType, joinedAt }],
        }),
      );
    },
    [dispatch],
  );

  const handleMembersAdd = useCallback(
    (socketData: SocketData<(InvitedMembers | SocketDataForTeam)[]>) => {
      const { data: members, projectId } = socketData;

      members.forEach((item) => {
        if ((item as InvitedMembers).user) {
          workerApi
            .addUserToProject((item as InvitedMembers).user.userId, projectId)
            .catch((err) =>
              logger.error('Error while adding user to the project', err),
            );
          addUserHandler(projectId, item as InvitedMembers);
        } else if ((item as SocketDataForTeam).identityType === 'TEAM') {
          addTeamToProjectSocketHandler(
            projectId,
            item as SocketDataForTeam,
            teamLookup,
          );
        }
      });
      onSuccess?.();
    },
    [
      addTeamToProjectSocketHandler,
      addUserHandler,
      onSuccess,
      teamLookup,
      workerApi,
    ],
  );

  const handleMembersDelete = useCallback(
    (
      socketData: SocketData<(UserSocketIdentifier | TeamSocketIdentifier)[]>,
    ) => {
      const { data, projectId } = socketData;

      data.forEach((item) => {
        if (item.identityType === 'USER') {
          workerApi
            .removeUserFromProject(item.userId, projectId)
            .catch((err) =>
              logger.error('Error removing user from the project', err),
            );

          dispatch(
            remove({
              projectId: ProjectId(projectId),
              userIds: [UserId(item.userId)],
            }),
          );
        } else if (item.identityType === 'TEAM') {
          removeTeamFromLocalState(projectId, item.teamId);
        }
      });
      onSuccess?.();
    },
    [dispatch, onSuccess, removeTeamFromLocalState, workerApi],
  );

  return {
    handleMembersAdd,
    handleMembersDelete,
  };
};
